/**
 * โหมด "สแกนป้าย Kanban" — อ่านป้าย `OUTPUT_TAG` แล้วบอกว่าควรทำต่อยังไง (D-170)
 *
 * ต่างจากสามโหมดเดิมของเครื่องสแกน: นี่คือการ**อ่านออนไลน์**ตรงไปเซิร์ฟเวอร์
 * (`resolveScan`, `lib/shopFloor/resolve.ts`) ไม่ใช่คำสั่งที่เข้าคิวออฟไลน์ —
 * เพราะ resolve อ่านอย่างเดียว ไม่มีอะไรให้ sync engine ส่งซ้ำ เครื่องต้อง
 * ออนไลน์เท่านั้นถึงจะใช้หน้าจอนี้ได้
 *
 * หน้าจอนี้**ไม่ส่งคำสั่ง** Scan-In/Scan-Out เอง — ปุ่มพาไปโมดูลเดิมพร้อมค่า
 * เริ่มต้นผ่าน URL search params เท่านั้น ผู้ปฏิบัติงานยังต้องยืนยัน/แก้ไขที่หน้า
 * นั้นเหมือนเดิมทุกประการ
 */

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { SCANNER_MODES } from './modes';
import { scannerModulePath } from './routes';
import { primaryTouchTargetStyle, touchTargetStyle } from './touch';
import { useConnectivity } from './useConnectivity';
import {
  parseScannedTagCode,
  resolveScan,
  type OutputTagNextAction,
  type ScanResolveOutputTag,
  type ScanResolveResult,
} from '../lib/shopFloor/resolve';

const NEXT_ACTION_LABEL: Record<OutputTagNextAction, string> = {
  WAIT_MATERIAL: 'รอวัตถุดิบเข้า Staging',
  SCAN_IN: 'พร้อมเริ่มผลิต',
  SCAN_OUT: 'กำลังผลิต',
  DONE: 'ผลิตป้ายนี้แล้ว',
  NOT_ACTIVE: 'ป้ายนี้ถูกยกเลิก/พิมพ์ใหม่ — ห้ามใช้',
  ORDER_NOT_RUNNABLE: 'ใบสั่งผลิตยังไม่พร้อมผลิต',
};

const NEXT_ACTION_TONE: Record<OutputTagNextAction, string> = {
  WAIT_MATERIAL: 'cap-tag-badge--warning',
  SCAN_IN: 'cap-tag-badge--success',
  SCAN_OUT: 'cap-tag-badge--info',
  DONE: 'cap-tag-badge--neutral',
  NOT_ACTIVE: 'cap-tag-badge--danger',
  ORDER_NOT_RUNNABLE: 'cap-tag-badge--warning',
};

type ScreenState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'invalid'; readonly message: string }
  | { readonly kind: 'result'; readonly outputTag: ScanResolveOutputTag }
  | { readonly kind: 'failed'; readonly message: string };

/** ข้อความไทยของแต่ละสถานะล้มเหลว — `cancelled` คืน `null` (ถูกยกเลิกเฉยๆ ไม่ต้องเตือน) */
function describeFailure(result: Exclude<ScanResolveResult, { status: 'ready' }>): string | null {
  switch (result.status) {
    case 'not-found':
      return 'ไม่พบป้าย (หรือไม่ใช่โรงงานของคุณ)';
    case 'rule-violation':
      return result.error.error_code === 'TAG_SCHEMA_VERSION_UNSUPPORTED'
        ? 'คิวอาร์รุ่นนี้ไม่รองรับแล้ว — พิมพ์ป้ายใหม่'
        : 'ป้ายนี้ใช้งานไม่ได้ในขณะนี้';
    case 'forbidden':
      return 'ไม่มีสิทธิ์ดูป้ายนี้';
    case 'unauthenticated':
      return 'เซสชันหมดอายุ — กรุณาเข้าสู่ระบบใหม่';
    case 'bad-request':
      return 'คำขอไม่ถูกต้อง';
    case 'server-error':
      return 'เซิร์ฟเวอร์ขัดข้อง — ลองใหม่อีกครั้ง';
    case 'network-error':
      return 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ตรวจสัญญาณแล้วลองใหม่';
    case 'invalid-response':
      return 'คำตอบจากเซิร์ฟเวอร์ผิดรูป';
    case 'cancelled':
      return null;
  }
}

/** path ของโมดูล Scan-In/Scan-Out จาก `SCANNER_MODES` — ไม่ประดิษฐ์ segment ซ้ำที่นี่ */
function modulePathFor(modeId: 'SCAN_IN' | 'SCAN_OUT'): string {
  const mode = SCANNER_MODES.find((option) => option.id === modeId);
  return scannerModulePath(mode === undefined ? modeId.toLowerCase().replace('_', '-') : mode.path);
}

interface TagScanResultCardProps {
  readonly outputTag: ScanResolveOutputTag;
  readonly onScanNext: () => void;
}

function TagScanResultCard({ outputTag, onScanNext }: TagScanResultCardProps) {
  const nextAction = outputTag.next_action;
  const query = `?order=${encodeURIComponent(outputTag.production_order_id)}&seq=${outputTag.operation_seq}`;
  /* เฉพาะปุ่ม Scan-Out — ป้ายที่กำลังปิดรอบคือป้ายนี้เอง (D-170, REV-009 F1); Scan-In ไม่เปลี่ยน */
  const scanOutQuery = `${query}&tag=${encodeURIComponent(outputTag.tag_code)}`;

  return (
    <div className="cap-tag-result" data-testid="tag-scan-result" data-next-action={nextAction}>
      <div
        className={`cap-tag-badge ${NEXT_ACTION_TONE[nextAction]}`}
        data-testid="tag-scan-next-action"
        role="status"
      >
        {NEXT_ACTION_LABEL[nextAction]}
      </div>

      <dl className="cap-scanner__facts">
        <div>
          <dt>แพ็คเกจ</dt>
          <dd data-testid="tag-scan-package">
            {outputTag.package_seq}/{outputTag.package_count}
          </dd>
        </div>
        <div>
          <dt>จำนวน</dt>
          <dd data-testid="tag-scan-qty">{outputTag.tag_qty}</dd>
        </div>
        <div>
          <dt>Part No.</dt>
          <dd data-testid="tag-scan-part">{outputTag.part_no}</dd>
        </div>
        <div>
          <dt>Lot No.</dt>
          <dd data-testid="tag-scan-lot">{outputTag.lot_no ?? 'ยังไม่ออก lot'}</dd>
        </div>
        <div>
          <dt>ใบสั่ง / ขั้น / Work Center</dt>
          <dd data-testid="tag-scan-order">
            {outputTag.order_no} · ขั้น {outputTag.operation_seq} ·{' '}
            {outputTag.work_center_code ?? '—'}
          </dd>
        </div>
        <div>
          <dt>Stage</dt>
          <dd data-testid="tag-scan-stage">{outputTag.stage}</dd>
        </div>
      </dl>

      {outputTag.materials.length > 0 ? (
        <div data-testid="tag-scan-materials">
          <p className="cap-scanner__note">วัตถุดิบที่ staged ที่ขั้นนี้:</p>
          <ul className="cap-scan-form__problems">
            {outputTag.materials.map((material) => (
              <li key={material.product_code}>
                {material.product_code} — เหลือ {material.remaining_qty}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {nextAction === 'SCAN_IN' ? (
        <Link
          className="cap-scanner__cta"
          style={primaryTouchTargetStyle}
          data-testid="tag-scan-go-scan-in"
          to={`${modulePathFor('SCAN_IN')}${query}`}
        >
          ไปสแกนเข้า (Scan-In)
        </Link>
      ) : null}

      {nextAction === 'SCAN_OUT' ? (
        <Link
          className="cap-scanner__cta"
          style={primaryTouchTargetStyle}
          data-testid="tag-scan-go-scan-out"
          to={`${modulePathFor('SCAN_OUT')}${scanOutQuery}`}
        >
          ไปสแกนออก (Scan-Out)
        </Link>
      ) : null}

      <button
        type="button"
        className="cap-scanner__sync-now"
        style={touchTargetStyle}
        data-testid="tag-scan-next"
        onClick={onScanNext}
      >
        สแกนใบต่อไป
      </button>
    </div>
  );
}

export function TagScanScreen() {
  const online = useConnectivity();
  const [code, setCode] = useState('');
  const [state, setState] = useState<ScreenState>({ kind: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);
  /* ยกเลิกผลของคำขอก่อนหน้าเมื่อผู้ใช้สแกนใบใหม่ก่อนคำขอเก่าจะตอบ */
  const requestIdRef = useRef(0);

  const reset = () => {
    setCode('');
    setState({ kind: 'idle' });
    inputRef.current?.focus();
  };

  const handleSubmit = async () => {
    const parsed = parseScannedTagCode(code);
    if (!parsed.ok) {
      setState({ kind: 'invalid', message: parsed.message });
      return;
    }
    if (!online) {
      return;
    }

    const requestId = ++requestIdRef.current;
    setState({ kind: 'loading' });
    const result = await resolveScan(parsed.tagCode, parsed.schemaVersion);
    if (requestId !== requestIdRef.current) {
      return;
    }

    if (result.status === 'ready') {
      setState({ kind: 'result', outputTag: result.outputTag });
      return;
    }
    const message = describeFailure(result);
    if (message !== null) {
      setState({ kind: 'failed', message });
    }
  };

  return (
    <section aria-label="สแกนป้าย Kanban" className="cap-scanner__scan" data-testid="tag-scan-screen">
      <h2 className="cap-scan-form__title">สแกนป้าย Kanban</h2>
      <p className="cap-scanner__note">
        สแกนหรือพิมพ์รหัสป้าย — การตรวจนี้อ่านสดจากเซิร์ฟเวอร์ ไม่เข้าคิวออฟไลน์
      </p>

      {!online ? (
        <p className="cap-scan-form__alert" role="alert" data-testid="tag-scan-offline">
          ต้องออนไลน์เพื่ออ่านป้าย — ป้ายนี้อ่านสดจากเซิร์ฟเวอร์เสมอ ไม่เข้าคิวออฟไลน์
        </p>
      ) : null}

      <form
        className="cap-scan-form"
        data-testid="tag-scan-form"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="cap-scan-field">
          <span className="cap-scan-field__label">รหัสป้าย (QR หรือ KB-P1-2611A-00001)</span>
          <input
            ref={inputRef}
            className="cap-scan-field__input"
            style={touchTargetStyle}
            type="text"
            autoFocus
            autoComplete="off"
            data-testid="tag-scan-input"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
          />
        </label>
        <button
          type="submit"
          className="cap-scanner__cta"
          style={primaryTouchTargetStyle}
          data-testid="tag-scan-submit"
          disabled={state.kind === 'loading' || !online}
        >
          {state.kind === 'loading' ? 'กำลังตรวจสอบ…' : 'ตรวจป้าย'}
        </button>
      </form>

      {state.kind === 'invalid' ? (
        <p className="cap-scan-form__alert" role="alert" data-testid="tag-scan-invalid">
          {state.message}
        </p>
      ) : null}

      {state.kind === 'failed' ? (
        <p className="cap-scan-form__alert" role="alert" data-testid="tag-scan-failed">
          {state.message}
        </p>
      ) : null}

      {state.kind === 'result' ? (
        <TagScanResultCard outputTag={state.outputTag} onScanNext={reset} />
      ) : null}
    </section>
  );
}
